import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScriptureAnchor } from '@/types/prayer';
import { usePrayScriptureDraft } from '@/hooks/usePrayScriptureDraft';
import { useSaveCue } from '@/hooks/useSaveCue';
import { inlineSaveOfferHeardNoWords, inlineSaveOfferFailedWithoutWords } from '@/lib/audio-queue';
import MicButton from '../MicButton';
import CopyButton from '../CopyButton';
import SheetShell from '../SheetShell';
import styles from './PrayOnScriptureSheet.module.css';

interface PrayOnScriptureSheetProps {
  anchor: ScriptureAnchor;
  onClose: () => void;
  onSaved: () => void;
  zIndex?: number;
}
/** Back has already popped the layer. The host restores it only when this
 * consumer must keep its confirmation, write or capture custody visible. */
export interface PrayScriptureSheetHandle { requestClose: () => 'keep' | 'close' }

const PrayOnScriptureSheet = forwardRef<PrayScriptureSheetHandle, PrayOnScriptureSheetProps>(
  function PrayOnScriptureSheet({ anchor, onClose, onSaved, zIndex }, ref) {
    const { t } = useTranslation();
    const editor = usePrayScriptureDraft(anchor);
    const { draft, state } = editor;
    const [saved, setSaved] = useState(false);
    const [confirmRevision, setConfirmRevision] = useState<number | null>(null);
    const [pauseConfirmation, setPauseConfirmation] = useState(false);
    const keepWritingRef = useRef<HTMLButtonElement>(null);
    const discardRef = useRef<HTMLButtonElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    const wasConfirming = useRef(false);
    const mounted = useRef(false);
    const pendingClose = useRef<'capture' | 'write' | null>(null);
    const showSaveCue = useSaveCue(editor.isListening || editor.isTranscribing, editor.displayValue);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
      if (confirmRevision !== null) keepWritingRef.current?.focus({ preventScroll: true });
      else if (wasConfirming.current) (discardRef.current?.disabled ? closeRef.current : discardRef.current)?.focus({ preventScroll: true });
      wasConfirming.current = confirmRevision !== null;
    }, [confirmRevision]);
    const requestClose = useCallback((): 'keep' | 'close' => {
      if (saved) { onSaved(); return 'close'; }
      if (confirmRevision !== null) { setConfirmRevision(null); return 'keep'; }
      if (draft.getSnapshot().operation) { pendingClose.current = 'write'; return 'keep'; }
      if (!editor.keepDraft()) { pendingClose.current = 'capture'; return 'keep'; }
      pendingClose.current = null; onClose(); return 'close';
    }, [saved, onSaved, confirmRevision, draft, editor, onClose]);
    useImperativeHandle(ref, () => ({ requestClose }), [requestClose]);
    useEffect(() => {
      if (pendingClose.current !== 'capture') return;
      if (state.capture === 'failed') { pendingClose.current = null; return; }
      if (state.capture === 'idle' && editor.keepDraft()) { pendingClose.current = null; onClose(); }
    }, [state.capture, editor, onClose]);
    useEffect(() => {
      if (!saved || pauseConfirmation) return;
      const timer = setTimeout(onSaved, 2600); return () => clearTimeout(timer);
    }, [saved, pauseConfirmation, onSaved]);
    const handleSave = async (separate = false) => {
      const success = await (separate ? editor.saveSeparate() : editor.save());
      if (!mounted.current || !draft.isCurrent()) return;
      const pending = pendingClose.current === 'write'; pendingClose.current = null;
      if (success && !draft.getSnapshot().text) {
        if (pending) onSaved(); else setSaved(true);
      }
    };
    const handleDiscard = async () => {
      if (confirmRevision === null) return;
      const success = await editor.discard(confirmRevision);
      if (!mounted.current || !draft.isCurrent()) return;
      setConfirmRevision(null);
      const pending = pendingClose.current === 'write'; pendingClose.current = null;
      if (success && pending) onClose();
    };
    const showActions = !editor.isListening && !editor.isTranscribing && state.capture !== 'stopping';
    return <SheetShell ariaLabel={t('prayScripture.sheetTitle')} onBackdrop={() => { requestClose(); }}
      closeLabel={t('summary.close')} zIndex={zIndex} dragToDismiss>
      <div className={styles.content}>
      {saved ? <div className={styles.confirm} role="status" aria-live="polite"
        onMouseEnter={() => setPauseConfirmation(true)} onMouseLeave={() => setPauseConfirmation(false)}
        onFocusCapture={() => setPauseConfirmation(true)} onBlurCapture={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) setPauseConfirmation(false);
        }}>
        <span className={styles.confirmMark} aria-hidden="true">✦</span>
        <span className={styles.confirmText}>{t('prayScripture.saved')}</span>
        <span className={styles.confirmRef}>{draft.anchor.reference}</span>
        <button type="button" className={styles.cancel} onClick={onSaved}>{t('updateDraft.close')}</button>
      </div> : <>
        <div className={styles.header}>
          <span className={styles.intro}>{t('prayScripture.intro')}</span>
          <span className={styles.reference}>{draft.anchor.reference}</span>
          <p className={styles.versePreview}>{draft.anchor.text}</p>
        </div>
        <div className={styles.body}>
          {confirmRevision !== null ? <div className={styles.discardConfirmation} role="group" aria-label={t('updateDraft.confirm')}>
            <p>{t('updateDraft.confirm')}</p>
            {draft.knownIds.size > 0 && <p>{t('updateDraft.recordingKept')}</p>}
            <div className={styles.draftActions}>
              <button ref={keepWritingRef} type="button" className={styles.cancel} disabled={Boolean(state.operation)} onClick={() => setConfirmRevision(null)}>{t('updateDraft.keepWriting')}</button>
              <button type="button" className={styles.confirmDiscard} disabled={Boolean(state.operation)} onClick={() => void handleDiscard()}>{t('updateDraft.discard')}</button>
            </div>
          </div> : <>
            <textarea className={styles.textarea} value={editor.displayValue} onChange={editor.handleChange}
              disabled={!editor.ready || Boolean(state.operation)} placeholder={t('prayScripture.placeholder')}
              aria-label={t('pray.prayerInputAria')} rows={4} />
            {state.loading !== 'ready' && <div className={styles.draftNote} role="status">
              <p>{t(`scriptureDraft.${state.loading === 'multiple' ? 'multiple' : state.loading === 'failed' ? 'loadFailed' : 'loading'}`, { section: t('prayerRecovery.title') })}</p>
              {state.loading !== 'loading' && <button type="button" className={styles.heldConflictAction} onClick={editor.retryLoad}>{t('updateDraft.retry')}</button>}
            </div>}
            {editor.ready && state.text && <p className={styles.draftNote}>{t('updateDraft.lifetime')}</p>}
            {state.resolution !== 'none' && <div className={styles.draftNote} role="status">
              <p>{t(`scriptureDraft.${state.resolution}`)}</p>
              {state.resolution !== 'saved' && <button type="button" className={styles.heldConflictAction} disabled={Boolean(state.operation)} onClick={editor.retryLoad}>{t('updateDraft.retry')}</button>}
            </div>}
            {state.discardError && <p className={styles.saveFailed} role="alert">{t('updateDraft.discardFailed')}</p>}
            {state.saveError && <p className={styles.saveFailed} role="alert">{t(state.saveReopen === 'words' ? 'voice.saveNeedsReopen' : state.saveReopen === 'recording' ? 'voice.saveRecordingNeedsReopen' : 'prayScripture.saveFailed')}</p>}
            {editor.recoveryError && <p className={styles.saveFailed} role="alert">{t('scriptureDraft.recoveryFailed')}</p>}
            {state.heldConflict && <p className={styles.saveFailed} role="alert">{t(state.heldConflict.kind === 'saved' ? 'voice.heldRecordingSaved' : 'voice.heldRecordingUnknown')}</p>}
            {state.capture !== 'idle' && <p className={styles.voiceNotice} role={state.capture === 'failed' ? 'alert' : 'status'}>{t(state.capture === 'failed' ? 'scriptureDraft.stopFailed' : 'scriptureDraft.finishing')}</p>}
            {state.noWords && !editor.isListening && !editor.isTranscribing && !editor.displayValue.trim() && <p className={styles.voiceNotice} role="status">
              {t(editor.offer ? inlineSaveOfferHeardNoWords(editor.offer) ? 'prayScripture.voiceNoSpeech'
                : inlineSaveOfferFailedWithoutWords(editor.offer) ? 'prayScripture.voiceFailed' : 'prayScripture.voiceNotReady' : 'prayScripture.voiceFailed')}
            </p>}
            {showActions && editor.recordingWords.length > 0 && <div className={styles.draftNote}>
              <p>{t('updateDraft.recordingKept')}</p>
              <button type="button" className={styles.heldConflictAction} disabled={!editor.ready || Boolean(state.operation)} onClick={editor.useRecordingWords}>{t('updateDraft.useRecordingWords')}</button>
            </div>}
            {editor.isSupported && <div className={styles.micArea}><MicButton size="lg" isListening={editor.isListening}
              isTranscribing={editor.isTranscribing} showLabel onClick={editor.toggleMic}
              disabled={!editor.ready || Boolean(state.operation)} ariaLabel={t(editor.isListening ? 'detail.stopRecording' : 'detail.voiceInput')} /></div>}
            {(state.capture !== 'idle' || (!editor.isListening && !editor.isTranscribing)) && state.text && <CopyButton text={state.text} label={t('detail.copyPrayerAria')} />}
            {showActions && <>
              {showSaveCue && !state.heldConflict && state.resolution === 'none' && <div className={styles.hint} aria-hidden="true">{t('prayScripture.hint')}</div>}
              <div className={styles.actions} data-keyboard-actions="">
                <button ref={closeRef} type="button" className={styles.cancel} onClick={() => { requestClose(); }} disabled={Boolean(state.operation)}>{t('updateDraft.close')}</button>
                {state.heldConflict || state.resolution === 'saved' ?
                  <button type="button" className={styles.save} disabled={!editor.ready || Boolean(state.operation) || !state.text.trim()} onClick={() => void handleSave(true)}>{t('voice.saveSeparatePrayerAction')}</button> :
                  <button type="button" className={`${styles.save} ${showSaveCue ? styles.saveCue : ''}`} disabled={!editor.canSave} onClick={() => void handleSave()}>{t(state.operation === 'save' ? 'prayScripture.saving' : 'prayScripture.save')}</button>}
              </div>
              <button ref={discardRef} type="button" className={styles.discardDraft} disabled={!editor.ready || Boolean(state.operation) || state.resolution === 'changed' || !state.text.trim()}
                onClick={() => setConfirmRevision(state.revision)}>{t('updateDraft.discard')}</button>
            </>}
          </>}
        </div>
      </>}
      </div>
    </SheetShell>;
  },
);
export default PrayOnScriptureSheet;
